import { ClimateDaySummary, ClimateMonthSummary, ClimateWeeklySnowpack, ClimatePointData, ClimateElevationProfile, ElevationAdjustmentDetail, Manifest } from '../types';
import * as pmtiles from 'pmtiles';
import { getTileUrl, getOrCreatePMTiles, appendAuthToken, getApiServer } from '../config';
import authService from './AuthService';
import { TierAccessService, TileAccessDenial } from './TierAccessService';

export class ClimateService {
  public static lastAccessDenial: TileAccessDenial | null = null;
  private static pointCache: Map<string, ClimatePointData> = new Map();
  private static pmtilesInstances: Map<string, pmtiles.PMTiles> = new Map();

  public static clearCache(): void {
    this.pointCache.clear();
    this.binaryTileCache.clear();
    this.pmtilesInstances.clear();
    this.lastAccessDenial = null;
  }

  /**
   * Retrieves or instantiates a cached PMTiles reader.
   */
  private static getPmtilesInstance(url: string): pmtiles.PMTiles {
    return getOrCreatePMTiles(url, this.pmtilesInstances);
  }

  /**
   * Returns Day of Year (1..365)
   */
  public static getDayOfYear(date: Date): number {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date.getTime() - start.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    const doy = Math.floor(diff / oneDay);
    return Math.max(1, Math.min(365, doy));
  }

  /**
   * Computes default date string and month index.
   */
  public static getDefaultDateInfo(nowDate: Date = new Date()): { dateStr: string; monthIndex: number; doy: number } {
    const yyyy = nowDate.getFullYear();
    const mm = String(nowDate.getMonth() + 1).padStart(2, '0');
    const dd = String(nowDate.getDate()).padStart(2, '0');
    const dateStr = `${yyyy}-${mm}-${dd}`;
    const monthIndex = nowDate.getMonth();
    const doy = this.getDayOfYear(nowDate);
    return { dateStr, monthIndex, doy };
  }

  // ==========================================
  // Lossless Palette Decoders (1:1 with color_schemes.py)
  // ==========================================

  // Continuous 3D RGB temperature segments matching apply_climate_temp_gradient in color_schemes.py
  private static readonly TEMP_SEGMENTS: [number, number, [number, number, number], [number, number, number]][] = [
    [-40.0, 0.0,   [106.0, 27.0, 154.0],  [21.0, 101.0, 192.0]],   // < 0°F (Deep Violet -> Navy)
    [0.0, 32.0,    [21.0, 101.0, 192.0],   [79.0, 195.0, 247.0]],  // 0 - 32°F (Navy -> Ice Blue)
    [32.0, 55.0,   [79.0, 195.0, 247.0],   [46.0, 125.0, 50.0]],   // 32 - 55°F (Ice Blue -> Emerald Green)
    [55.0, 75.0,   [46.0, 125.0, 50.0],    [255.0, 179.0, 0.0]],   // 55 - 75°F (Emerald Green -> Golden Amber)
    [75.0, 90.0,   [255.0, 179.0, 0.0],    [244.0, 81.0, 30.0]],   // 75 - 90°F (Golden Amber -> Vivid Coral Orange)
    [90.0, 105.0,  [244.0, 81.0, 30.0],    [183.0, 28.0, 28.0]],   // 90 - 105°F (Coral Orange -> Crimson Red)
    [105.0, 125.0, [183.0, 28.0, 28.0],    [255.0, 150.0, 220.0]]  // > 105°F (Crimson -> Hot Magenta / White)
  ];

  public static decodeTempPixel(r: number, g: number, b: number, a: number): number | null {
    if (a < 50) return null;
    let bestDistSq = Infinity;
    let bestTemp = 0;

    for (const [t0, t1, p0, p1] of this.TEMP_SEGMENTS) {
      const vx = p1[0] - p0[0];
      const vy = p1[1] - p0[1];
      const vz = p1[2] - p0[2];
      const vLenSq = vx * vx + vy * vy + vz * vz;
      if (vLenSq === 0) continue;

      const dot = (r - p0[0]) * vx + (g - p0[1]) * vy + (b - p0[2]) * vz;
      const tProj = Math.max(0, Math.min(1, dot / vLenSq));

      const projX = p0[0] + tProj * vx;
      const projY = p0[1] + tProj * vy;
      const projZ = p0[2] + tProj * vz;

      const distSq = (r - projX) * (r - projX) + (g - projY) * (g - projY) + (b - projZ) * (b - projZ);
      if (distSq < bestDistSq) {
        bestDistSq = distSq;
        bestTemp = t0 + tProj * (t1 - t0);
      }
    }

    return Math.round(bestTemp * 10) / 10;
  }

  public static decodeDaysCountPixel(_r: number, _g: number, _b: number, a: number, _isHot: boolean = true): number {
    if (a < 50) return 0;
    const t = (a - 150) / (255 - 150);
    return Math.max(0, Math.min(31, Math.round(1 + Math.max(0, Math.min(1, t)) * 30)));
  }

  public static decodePrecipMonthlyPixel(r: number, _g: number, _b: number, a: number): number {
    if (a < 50) return 0.0;
    if (a <= 190) {
      const t = (a - 150) / 40.0;
      return Math.round((0.2 + Math.max(0, Math.min(1, t)) * 1.8) * 100) / 100;
    }
    if (a <= 230) {
      const t = (a - 190) / 40.0;
      return Math.round((2.0 + Math.max(0, Math.min(1, t)) * 4.0) * 100) / 100;
    }
    if (a < 255) {
      const t = (a - 230) / 25.0;
      return Math.round((6.0 + Math.max(0, Math.min(1, t)) * 6.0) * 100) / 100;
    }
    const t = (r - 109) / (190 - 109);
    return Math.round((12.0 + Math.max(0, Math.min(1, t)) * 13.0) * 100) / 100;
  }

  public static decodeSnowDepthPixel(r: number, g: number, _b: number, a: number): number {
    if (a < 50) return 0.0;
    if (a < 215) {
      const t = (a - 180.0) / 35.0;
      return Math.round((0.5 + Math.max(0, Math.min(1, t)) * 5.5) * 10) / 10;
    }
    if (a < 245) {
      const t = (a - 215.0) / 30.0;
      return Math.round((6.0 + Math.max(0, Math.min(1, t)) * 18.0) * 10) / 10;
    }
    if (a < 255) {
      const t = (a - 245.0) / 10.0;
      return Math.round((24.0 + Math.max(0, Math.min(1, t)) * 36.0) * 10) / 10;
    }
    // a === 255
    if (r < 180) {
      const t = (r - 126.0) / (162.0 - 126.0);
      return Math.round((60.0 + Math.max(0, Math.min(1, t)) * 60.0) * 10) / 10;
    }
    // > 120 inches: decode using Green channel up to 400"+
    const t = (g - 114.0) / (255.0 - 114.0);
    return Math.round((120.0 + Math.max(0, Math.min(1, t)) * 280.0) * 10) / 10;
  }

  public static decodeWindPixel(_r: number, _g: number, b: number, a: number): number {
    if (a < 50) return 2.0;
    if (a < 180) {
      const t = (a - 140.0) / 40.0;
      return Math.round((4.0 + Math.max(0, Math.min(1, t)) * 4.0) * 10) / 10;
    }
    if (a < 210) {
      const t = (a - 180.0) / 30.0;
      return Math.round((8.0 + Math.max(0, Math.min(1, t)) * 4.0) * 10) / 10;
    }
    if (a < 235) {
      const t = (a - 210.0) / 25.0;
      return Math.round((12.0 + Math.max(0, Math.min(1, t)) * 4.0) * 10) / 10;
    }
    if (a < 255) {
      const t = (a - 235.0) / 20.0;
      return Math.round((16.0 + Math.max(0, Math.min(1, t)) * 4.0) * 10) / 10;
    }
    // a === 255: 20 to 26+ mph
    if (b > 220) return 26.0;
    return 22.0;
  }

  /**
   * Samples a discrete metric pixel directly from a PMTiles raster layer.
   */
  public static async sampleClimateLayerPixel(
    manifest: Manifest,
    variable: string,
    timeKey: string,
    mode: 'daily' | 'monthly' | 'weekly',
    lat: number,
    lon: number,
    zoom: number = 5
  ): Promise<number | null> {
    try {
      const tileUrl = getTileUrl(manifest, 'climate', variable, timeKey, mode);
      if (!tileUrl) return null;

      const pmt = this.getPmtilesInstance(tileUrl);
      const n = 1 << zoom;
      const xExact = ((lon + 180.0) / 360.0) * n;
      const latRad = (lat * Math.PI) / 180.0;
      const yExact = (1.0 - Math.log(Math.tan(latRad) + 1.0 / Math.cos(latRad)) / Math.PI) / 2.0 * n;
      const tileX = Math.floor(xExact);
      const tileY = Math.floor(yExact);
      const px = Math.floor((xExact - tileX) * 256);
      const py = Math.floor((yExact - tileY) * 256);

      const tileResp = await pmt.getZxy(zoom, tileX, tileY);
      if (!tileResp || !tileResp.data) return null;

      const blob = new Blob([tileResp.data], { type: 'image/webp' });
      let imgBitmap: ImageBitmap | HTMLImageElement;
      if (typeof createImageBitmap === 'function') {
        imgBitmap = await createImageBitmap(blob);
      } else {
        imgBitmap = await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = URL.createObjectURL(blob);
        });
      }

      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      ctx.drawImage(imgBitmap, 0, 0);
      const pixel = ctx.getImageData(Math.max(0, Math.min(255, px)), Math.max(0, Math.min(255, py)), 1, 1).data;
      const [r, g, b, a] = pixel;

      const isMonthly = mode === 'monthly' || timeKey.startsWith('m') || variable.endsWith('_monthly');
      if (variable === 'sunshine_pct' || variable === 'sunshine_pct_monthly') {
        return this.decodeSunshinePctPixel(r, g, b, a);
      }
      if (variable === 'solar_energy' || variable === 'solar_energy_monthly') {
        return this.decodeSolarEnergyPixel(r, g, b, a);
      }
      if (isMonthly && (variable.includes('sunny') || variable.includes('cloudy') || variable.includes('overcast'))) {
        return this.decodeDaysCountPixel(r, g, b, a);
      }
      if (variable.startsWith('days_gt_') || variable.startsWith('days_lt_') || variable === 'precip_days') {
        return this.decodeDaysCountPixel(r, g, b, a, variable.startsWith('days_gt_'));
      }
      if (variable === 'precip_monthly' || variable === 'precip') {
        return this.decodePrecipMonthlyPixel(r, g, b, a);
      }
      if (variable === 'snow_monthly' || variable === 'snow' || variable === 'snowpack' || variable === 'snow_swe') {
        return this.decodeSnowDepthPixel(r, g, b, a);
      }
      if (variable === 'wind_speed' || variable === 'wind') {
        return this.decodeWindPixel(r, g, b, a);
      }
      if (variable === 'precip_prob') {
        return this.decodePrecipProbPixel(r, g, b, a);
      }
      if (variable === 'mostly_sunny') {
        return this.decodeMostlySunnyPixel(r, g, b, a);
      }
      if (variable === 'partly_cloudy') {
        return this.decodePartlyCloudyPixel(r, g, b, a);
      }
      if (variable === 'overcast') {
        return this.decodeOvercastPixel(r, g, b, a);
      }
      if (variable.includes('temp') || variable.startsWith('tmax') || variable.startsWith('tmin')) {
        return this.decodeTempPixel(r, g, b, a);
      }
      return null;
    } catch {
      return null;
    }
  }

  public static decodeMostlySunnyPixel(_r: number, _g: number, _b: number, a: number): number {
    if (a < 50) return 0;
    if (a <= 180) {
      const t = (a - 150.0) / 30.0;
      return Math.round(5.0 + Math.max(0, Math.min(1, t)) * 20.0);
    }
    if (a <= 210) {
      const t = (a - 180.0) / 30.0;
      return Math.round(25.0 + Math.max(0, Math.min(1, t)) * 25.0);
    }
    if (a <= 240) {
      const t = (a - 210.0) / 30.0;
      return Math.round(50.0 + Math.max(0, Math.min(1, t)) * 25.0);
    }
    const t = (a - 240.0) / 15.0;
    return Math.round(75.0 + Math.max(0, Math.min(1, t)) * 25.0);
  }

  public static decodePartlyCloudyPixel(r: number, g: number, b: number, a: number): number {
    return this.decodeMostlySunnyPixel(r, g, b, a);
  }

  public static decodeOvercastPixel(_r: number, _g: number, _b: number, a: number): number {
    if (a < 50) return 0;
    if (a <= 215) {
      const t = Math.max(0, Math.min(1, (a - 195.0) / 20.0));
      return Math.round(5.0 + t * 20.0);
    }
    if (a <= 235) {
      const t = Math.max(0, Math.min(1, (a - 215.0) / 20.0));
      return Math.round(25.0 + t * 25.0);
    }
    if (a <= 248) {
      const t = Math.max(0, Math.min(1, (a - 235.0) / 13.0));
      return Math.round(50.0 + t * 25.0);
    }
    const t = Math.max(0, Math.min(1, (a - 248.0) / 7.0));
    return Math.round(75.0 + t * 25.0);
  }

  public static decodeSunshinePctPixel(_r: number, _g: number, _b: number, a: number): number {
    if (a < 50) return 0;
    if (a <= 215) {
      const t = Math.max(0, Math.min(1, (a - 195.0) / 20.0));
      return Math.round(5.0 + t * 20.0);
    }
    if (a <= 228) {
      const t = Math.max(0, Math.min(1, (a - 215.0) / 13.0));
      return Math.round(25.0 + t * 20.0);
    }
    if (a <= 238) {
      const t = Math.max(0, Math.min(1, (a - 228.0) / 10.0));
      return Math.round(45.0 + t * 15.0);
    }
    if (a <= 246) {
      const t = Math.max(0, Math.min(1, (a - 238.0) / 8.0));
      return Math.round(60.0 + t * 15.0);
    }
    const t = Math.max(0, Math.min(1, (a - 246.0) / 9.0));
    return Math.round(75.0 + t * 25.0);
  }

  public static decodeSolarEnergyPixel(_r: number, _g: number, _b: number, a: number): number {
    if (a < 50) return 0.0;
    if (a <= 218) {
      const t = Math.max(0, Math.min(1, (a - 195.0) / 23.0));
      return Math.round((0.2 + t * 1.3) * 10) / 10;
    }
    if (a <= 235) {
      const t = Math.max(0, Math.min(1, (a - 218.0) / 17.0));
      return Math.round((1.5 + t * 1.5) * 10) / 10;
    }
    if (a <= 245) {
      const t = Math.max(0, Math.min(1, (a - 235.0) / 10.0));
      return Math.round((3.0 + t * 2.0) * 10) / 10;
    }
    if (a <= 250) {
      const t = Math.max(0, Math.min(1, (a - 245.0) / 5.0));
      return Math.round((5.0 + t * 2.5) * 10) / 10;
    }
    const t = Math.max(0, Math.min(1, (a - 250.0) / 5.0));
    return Math.round((7.5 + t * 2.5) * 10) / 10;
  }

  public static decodePrecipProbPixel(_r: number, _g: number, _b: number, a: number): number {
    if (a < 50) return 0;
    if (a <= 180) {
      const t = (a - 140.0) / 40.0;
      return Math.round(5.0 + Math.max(0, Math.min(1, t)) * 15.0);
    }
    if (a <= 220) {
      const t = (a - 180.0) / 40.0;
      return Math.round(20.0 + Math.max(0, Math.min(1, t)) * 25.0);
    }
    if (a < 255) {
      const t = (a - 220.0) / 30.0;
      return Math.round(45.0 + Math.max(0, Math.min(1, t)) * 30.0);
    }
    return 85;
  }

  /**
   * Samples all days of a specific month for a variable at a coordinate directly from PMTiles.
   */
  public static async sampleMonthDays(
    manifest: Manifest,
    variable: string,
    year: number,
    monthIndex: number,
    lat: number,
    lon: number,
    _zoom: number = 5
  ): Promise<(number | null)[]> {
    const isSunshineVar = variable === 'mostly_sunny' || variable === 'partly_cloudy' || variable === 'overcast' || variable === 'sunshine_pct' || variable === 'solar_energy';
    if (isSunshineVar) {
      const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
      const startDoy = this.getDayOfYear(new Date(year, monthIndex, 1));
      const promises = Array.from({ length: daysInMonth }, async (_, dayIdx) => {
        const doy = startDoy + dayIdx;
        const timeKey = `doy_${String(doy).padStart(3, '0')}`;
        return this.sampleClimateLayerPixel(manifest, variable, timeKey, 'daily', lat, lon);
      });
      return Promise.all(promises);
    }

    const pointData = await this.fetchClimatePointData(manifest, lat, lon);
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    if (!pointData || !pointData.days) {
      return new Array(daysInMonth).fill(null);
    }

    const isRec = variable.includes('rec') || variable.includes('record');
    const isP90 = variable.includes('p90') || variable.includes('90');
    const isMin = variable.includes('tmin') || variable.includes('min');
    const isPrecip = variable.includes('prcp') || variable.includes('precip');

    const result: (number | null)[] = [];
    for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
      const curDate = new Date(year, monthIndex, dayNum);
      const doy = this.getDayOfYear(curDate);
      const day = pointData.days.find(d => d.doy === doy);
      if (day) {
        if (isPrecip) result.push(day.precipProb);
        else if (isMin) result.push(isRec ? day.tminRec : (isP90 ? day.tminP90 : day.tminMean));
        else result.push(isRec ? day.tmaxRec : (isP90 ? day.tmaxP90 : day.tmaxMean));
      } else {
        result.push(null);
      }
    }
    return result;
  }

  /**
   * Samples all 12 monthly values for a variable at a coordinate directly from binary spatial tiles.
   */
  public static async sampleAllMonths(
    manifest: Manifest,
    variable: string,
    lat: number,
    lon: number,
    _zoom: number = 5
  ): Promise<(number | null)[]> {
    const isSunshineMonthly = variable === 'mostly_sunny' || variable === 'partly_cloudy' || variable === 'overcast' ||
                              variable === 'mostly_sunny_monthly' || variable === 'partly_cloudy_monthly' || variable === 'overcast_monthly' ||
                              variable === 'sunshine_pct' || variable === 'sunshine_pct_monthly' ||
                              variable === 'solar_energy' || variable === 'solar_energy_monthly';
    if (isSunshineMonthly) {
      const prod = variable.replace('_monthly', '');
      const promises = Array.from({ length: 12 }, async (_, mIdx) => {
        const timeKey = `m${String(mIdx + 1).padStart(2, '0')}`;
        const val = await this.sampleClimateLayerPixel(manifest, prod, timeKey, 'monthly', lat, lon);
        return val !== null ? val : 0;
      });
      return Promise.all(promises);
    }

    const pointData = await this.fetchClimatePointData(manifest, lat, lon);
    if (!pointData || !pointData.months || pointData.months.length === 0) {
      return new Array(12).fill(null);
    }

    if (variable.startsWith('tmax') || variable.startsWith('tmin')) {
      const isRec = variable.includes('rec') || variable.includes('record');
      const isP90 = variable.includes('p90') || variable.includes('90');
      const isMin = variable.includes('tmin') || variable.includes('min');

      return Array.from({ length: 12 }, (_, mIdx) => {
        const mDays = pointData.days.filter(d => d.monthNum === mIdx + 1);
        if (mDays.length === 0) return 0;
        const sum = mDays.reduce((acc, d) => {
          const v = isMin ? (isRec ? d.tminRec : isP90 ? d.tminP90 : d.tminMean)
                          : (isRec ? d.tmaxRec : isP90 ? d.tmaxP90 : d.tmaxMean);
          return acc + v;
        }, 0);
        return Math.round(sum / mDays.length);
      });
    }

    return pointData.months.map(m => {
      switch (variable) {
        case 'precip_monthly': return m.precipInches;
        case 'snow_monthly': return m.snowInches;
        case 'precip_days': return m.precipDays;
        case 'days_gt_90': return m.daysGt90;
        case 'days_gt_95': return m.daysGt95;
        case 'days_gt_100': return m.daysGt100;
        case 'days_gt_105': return m.daysGt105;
        case 'days_gt_110': return m.daysGt110;
        case 'days_lt_40': return m.daysLt40;
        case 'days_lt_30': return m.daysLt30;
        case 'days_lt_20': return m.daysLt20;
        case 'days_lt_10': return m.daysLt10;
        case 'days_lt_0': return m.daysLt0;
        case 'wind_speed': return m.windSpeedMph;
        default: return 0;
      }
    });
  }

  /**
   * Samples all 52 weekly snowpack values for a coordinate directly from binary spatial tiles.
   */
  public static async sampleAllWeeks(
    manifest: Manifest,
    variable: string,
    lat: number,
    lon: number,
    _zoom: number = 5
  ): Promise<(number | null)[]> {
    const pointData = await this.fetchClimatePointData(manifest, lat, lon);
    if (!pointData || !pointData.weeks || pointData.weeks.length === 0) {
      return new Array(52).fill(null);
    }

    return pointData.weeks.map(w => {
      if (variable === 'snow_swe') {
        return w.snowSweIn;
      }
      return w.snowDepthIn;
    });
  }

  private static binaryTileCache: Map<string, ArrayBuffer | null> = new Map();

  /**
   * Generates calendar month skeleton structure for UI rendering.
   */
  public static getMonthCalendar(
    monthIndex: number,
    _lat: number,
    _lon: number,
    _elevFt: number,
    selectedDateStr: string,
    year: number = 2026
  ): { days: ClimateDaySummary[]; firstDayCol: number; monthName: string } {
    const dateObj = new Date(year, monthIndex, 1);
    const monthName = dateObj.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const firstDayCol = new Date(year, monthIndex, 1).getDay();
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const todayStr = this.getDefaultDateInfo().dateStr;

    const days: ClimateDaySummary[] = [];

    for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
      const curDate = new Date(year, monthIndex, dayNum);
      const yyyy = curDate.getFullYear();
      const mm = String(curDate.getMonth() + 1).padStart(2, '0');
      const dd = String(dayNum).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;
      const doy = this.getDayOfYear(curDate);

      days.push({
        doy,
        dateStr,
        dayNum,
        monthNum: monthIndex + 1,
        tmaxMean: 0,
        tmaxP90: 0,
        tmaxRec: 0,
        tminMean: 0,
        tminP90: 0,
        tminRec: 0,
        precipProb: 0,
        isSelected: dateStr === selectedDateStr,
        isToday: dateStr === todayStr
      });
    }

    return { days, firstDayCol, monthName };
  }

  /**
   * Computes microclimate elevation adjustments for a specific altitude relative to grid base elevation.
   */
  public static computeElevationAdjustment(
    annualAvgTmax: number,
    annualAvgTmin: number,
    annualTotalPrecip: number,
    baseSnowpack: number,
    targetElevFt: number,
    baseElevFt: number,
    monthlySummaries: { tmax: number; tmin: number; precipInches: number }[]
  ): ElevationAdjustmentDetail {
    const deltaFt = targetElevFt - baseElevFt;
    // Environmental lapse rate: -3.56°F per 1000 ft
    const tempDelta = (deltaFt / 1000.0) * -3.56;
    const adjTmax = Math.round((annualAvgTmax + tempDelta) * 10) / 10;
    const adjTmin = Math.round((annualAvgTmin + tempDelta) * 10) / 10;

    // Orographic precipitation enhancement: +5% per 1000 ft gain
    const precipMult = Math.max(0.2, Math.min(2.5, 1.0 + (deltaFt / 1000.0) * 0.05));
    const adjPrecip = Math.round(annualTotalPrecip * precipMult * 100) / 100;

    // Snowfall computed across all 12 months at target elevation
    let totalSnowInches = 0.0;
    for (const m of monthlySummaries) {
      const mTmax = m.tmax + tempDelta;
      const mTmin = m.tmin + tempDelta;
      const mMean = (mTmax + mTmin) / 2.0;
      const mPrecip = m.precipInches * precipMult;

      let snowRatio = 0.0;
      if (mMean <= 28.0) {
        snowRatio = 1.0;
      } else if (mMean < 40.0) {
        snowRatio = (40.0 - mMean) / 12.0;
      }

      totalSnowInches += mPrecip * snowRatio * 10.0;
    }
    const adjSnow = Math.round(totalSnowInches * 10) / 10;
    
    // Snowpack retention multiplier
    const snowpackMult = Math.max(0.0, 1.0 + (deltaFt / 1000.0) * 0.15);
    const adjSnowpack = Math.round(baseSnowpack * snowpackMult * 10) / 10;

    return {
      tmaxF: adjTmax,
      tminF: adjTmin,
      precipMult: Math.round(precipMult * 100) / 100,
      precipInches: adjPrecip,
      snowInches: adjSnow,
      snowpackInches: adjSnowpack
    };
  }

  /**
   * Fetches real binary spatial tile (climate_tiles/4/{x}/{y}.bin) and extracts full 365-day climatology profile.
   */
  public static async fetchClimatePointData(
    manifest: Manifest,
    lat: number,
    lon: number,
    elevFt: number = 1000,
    minElevFt?: number,
    maxElevFt?: number,
    zoom: number = 11
  ): Promise<ClimatePointData | null> {
    const effMinElev = minElevFt !== undefined && minElevFt >= 0 ? minElevFt : Math.max(0, Math.round(elevFt - 1200));
    const effMaxElev = maxElevFt !== undefined && maxElevFt >= 0 ? maxElevFt : Math.round(elevFt + 1800);

    const cacheKey = `${lat.toFixed(3)}_${lon.toFixed(3)}_${Math.round(elevFt)}_${Math.round(effMinElev)}_${Math.round(effMaxElev)}`;
    if (this.pointCache.has(cacheKey)) {
      return this.pointCache.get(cacheKey)!;
    }

    const n = 1 << zoom;
    const xExact = ((lon + 180.0) / 360.0) * n;
    const latRad = (lat * Math.PI) / 180.0;
    const yExact = (1.0 - Math.log(Math.tan(latRad) + 1.0 / Math.cos(latRad)) / Math.PI) / 2.0 * n;
    const tileX = Math.floor(xExact);
    const tileY = Math.floor(yExact);

    if (tileX < 0 || tileX >= n || tileY < 0 || tileY >= n) {
      return null;
    }

    const apiServer = getApiServer(manifest);
    const tileBase = manifest?.tile_base || 'tiles';
    const climatePath = manifest?.climate?.tile_path || 'climate_tiles';
    const cleanServer = apiServer.replace(/\/$/, '');
    let cleanPath = `${tileBase}/${climatePath}/${zoom}/${tileX}/${tileY}.bin`.replace(/\/+/g, '/');
    if (cleanPath.startsWith('/')) cleanPath = cleanPath.substring(1);
    const tileUrl = `${cleanServer}/${cleanPath}`;
    const authenticatedUrl = appendAuthToken(tileUrl);
    const token = authService.getSessionToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let buffer: ArrayBuffer | null = null;
    if (this.binaryTileCache.has(tileUrl)) {
      buffer = this.binaryTileCache.get(tileUrl)!;
    } else {
      try {
        const resp = await fetch(authenticatedUrl, {
          credentials: 'include',
          headers
        });
        if (resp.ok) {
          let rawBuffer = await resp.arrayBuffer();
          if (rawBuffer.byteLength >= 2) {
            const headerBytes = new Uint8Array(rawBuffer, 0, 2);
            if (headerBytes[0] === 0x1f && headerBytes[1] === 0x8b) {
              if (typeof DecompressionStream !== 'undefined') {
                const stream = new Response(rawBuffer).body?.pipeThrough(new DecompressionStream('gzip'));
                if (stream) {
                  rawBuffer = await new Response(stream).arrayBuffer();
                }
              } else {
                console.warn('[ClimateService] Native DecompressionStream not supported in this environment');
              }
            }
          }
          buffer = rawBuffer;
          this.lastAccessDenial = null;
          this.binaryTileCache.set(tileUrl, buffer);
        } else {
          const denial = TierAccessService.parseTileAccessDenial(resp, 'registered');
          if (denial) {
            this.lastAccessDenial = denial;
          } else {
            this.binaryTileCache.set(tileUrl, null);
          }
        }
      } catch {
        this.binaryTileCache.set(tileUrl, null);
      }
    }

    if (!buffer || buffer.byteLength < 32) {
      return null;
    }

    const view = new DataView(buffer);
    const magicStr = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
    if (magicStr !== 'GWCL') return null;

    const gridW = view.getUint16(8, true);
    const gridH = view.getUint16(10, true);
    const minLat = view.getFloat32(16, true);
    const maxLat = view.getFloat32(20, true);
    const minLon = view.getFloat32(24, true);
    const maxLon = view.getFloat32(28, true);

    if (lat < minLat || lat > maxLat || lon < minLon || lon > maxLon) {
      return null;
    }

    const px = Math.max(0, Math.min(gridW - 1, Math.floor((lon - minLon) / (maxLon - minLon) * gridW)));
    const py = Math.max(0, Math.min(gridH - 1, Math.floor((maxLat - lat) / (maxLat - minLat) * gridH)));
    const cellIdx = py * gridW + px;
    const numCells = gridW * gridH;

    let byteOffset = 32;

    const tmaxRecArr = new Int16Array(buffer, byteOffset, 365 * numCells); byteOffset += 365 * numCells * 2;
    const tmaxP90Arr = new Int16Array(buffer, byteOffset, 365 * numCells); byteOffset += 365 * numCells * 2;
    const tmaxMeanArr = new Int16Array(buffer, byteOffset, 365 * numCells); byteOffset += 365 * numCells * 2;
    const tminMeanArr = new Int16Array(buffer, byteOffset, 365 * numCells); byteOffset += 365 * numCells * 2;
    const tminP90Arr = new Int16Array(buffer, byteOffset, 365 * numCells); byteOffset += 365 * numCells * 2;
    const tminRecArr = new Int16Array(buffer, byteOffset, 365 * numCells); byteOffset += 365 * numCells * 2;
    const precipProbArr = new Uint8Array(buffer, byteOffset, 365 * numCells); byteOffset += 365 * numCells * 1;

    const mPrecipArr = new Uint16Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 2;
    const mSnowArr = new Uint16Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 2;
    const mPrecipDaysArr = new Uint8Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 1;
    const mDaysGt90Arr = new Uint8Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 1;
    const mDaysGt95Arr = new Uint8Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 1;
    const mDaysGt100Arr = new Uint8Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 1;
    const mDaysGt105Arr = new Uint8Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 1;
    const mDaysGt110Arr = new Uint8Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 1;
    const mDaysLt40Arr = new Uint8Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 1;
    const mDaysLt30Arr = new Uint8Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 1;
    const mDaysLt20Arr = new Uint8Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 1;
    const mDaysLt10Arr = new Uint8Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 1;
    const mDaysLt0Arr = new Uint8Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 1;
    const mWindSpeedArr = new Uint8Array(buffer, byteOffset, 12 * numCells); byteOffset += 12 * numCells * 1;

    const wSnowDepthArr = new Uint16Array(buffer, byteOffset, 52 * numCells); byteOffset += 52 * numCells * 2;
    const wSnowSweArr = new Uint16Array(buffer, byteOffset, 52 * numCells); byteOffset += 52 * numCells * 2;

    const days: ClimateDaySummary[] = [];
    for (let doy = 1; doy <= 365; doy++) {
      const idx = (doy - 1) * numCells + cellIdx;
      const estMonth = Math.min(12, Math.floor((doy - 1) / 30.5) + 1);
      const estDay = ((doy - 1) % 30) + 1;
      const dateStr = `2026-${String(estMonth).padStart(2, '0')}-${String(estDay).padStart(2, '0')}`;

      days.push({
        doy,
        dateStr,
        dayNum: estDay,
        monthNum: estMonth,
        tmaxMean: tmaxMeanArr[idx],
        tmaxP90: tmaxP90Arr[idx],
        tmaxRec: tmaxRecArr[idx],
        tminMean: tminMeanArr[idx],
        tminP90: tminP90Arr[idx],
        tminRec: tminRecArr[idx],
        precipProb: precipProbArr[idx]
      });
    }

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const months: ClimateMonthSummary[] = [];
    for (let m = 0; m < 12; m++) {
      const idx = m * numCells + cellIdx;
      months.push({
        month: m + 1,
        monthName: monthNames[m],
        precipInches: Math.round(mPrecipArr[idx] / 100.0 * 100) / 100,
        snowInches: Math.round(mSnowArr[idx] / 10.0 * 10) / 10,
        precipDays: mPrecipDaysArr[idx],
        daysGt90: mDaysGt90Arr[idx],
        daysGt95: mDaysGt95Arr[idx],
        daysGt100: mDaysGt100Arr[idx],
        daysGt105: mDaysGt105Arr[idx],
        daysGt110: mDaysGt110Arr[idx],
        daysLt40: mDaysLt40Arr[idx],
        daysLt30: mDaysLt30Arr[idx],
        daysLt20: mDaysLt20Arr[idx],
        daysLt10: mDaysLt10Arr[idx],
        daysLt0: mDaysLt0Arr[idx],
        windSpeedMph: mWindSpeedArr[idx]
      });
    }

    const weeks: ClimateWeeklySnowpack[] = [];
    for (let w = 1; w <= 52; w++) {
      const idx = (w - 1) * numCells + cellIdx;
      const approxMonth = Math.min(12, Math.floor((w - 1) / 4.33) + 1);
      const weekInMonth = ((w - 1) % 4) + 1;
      const weekLabel = `${monthNames[approxMonth - 1].slice(0, 3)} W${weekInMonth}`;

      weeks.push({
        week: w,
        weekLabel,
        snowDepthIn: Math.round(wSnowDepthArr[idx] / 10.0 * 10) / 10,
        snowSweIn: Math.round(wSnowSweArr[idx] / 10.0 * 10) / 10
      });
    }

    const annualAvgTmax = days.reduce((a, b) => a + b.tmaxMean, 0) / 365.0;
    const annualAvgTmin = days.reduce((a, b) => a + b.tminMean, 0) / 365.0;
    const annualTotalPrecip = months.reduce((a, b) => a + b.precipInches, 0);
    const maxSnowpack = Math.max(...weeks.map(w => w.snowDepthIn));

    const monthlySummariesForElev = months.map(m => ({
      tmax: annualAvgTmax,
      tmin: annualAvgTmin,
      precipInches: m.precipInches
    }));

    const valleyAdj = this.computeElevationAdjustment(annualAvgTmax, annualAvgTmin, annualTotalPrecip, maxSnowpack, effMinElev, elevFt, monthlySummariesForElev);
    const baseAdj = this.computeElevationAdjustment(annualAvgTmax, annualAvgTmin, annualTotalPrecip, maxSnowpack, elevFt, elevFt, monthlySummariesForElev);
    const peakAdj = this.computeElevationAdjustment(annualAvgTmax, annualAvgTmin, annualTotalPrecip, maxSnowpack, effMaxElev, elevFt, monthlySummariesForElev);

    const elevationProfile: ClimateElevationProfile = {
      minElevFt: effMinElev,
      baseElevFt: elevFt,
      maxElevFt: effMaxElev,
      deltaMinFt: effMinElev - elevFt,
      deltaMaxFt: effMaxElev - elevFt,
      valleyAdj,
      baseAdj,
      peakAdj
    };

    const result: ClimatePointData = {
      lat,
      lon,
      gridElevFt: elevFt,
      minElevFt: effMinElev,
      maxElevFt: effMaxElev,
      days,
      months,
      weeks,
      elevationProfile
    };

    this.pointCache.set(cacheKey, result);
    return result;
  }

  /**
   * Fetches dedicated binary tile (climate_tiles/{product}/11/{x}/{y}.bin) and extracts full 365-day probability curve and 12-month counts.
   */
  public static async fetchDedicatedClimateSunshineData(
    manifest: Manifest,
    product: 'mostly_sunny' | 'partly_cloudy' | 'overcast',
    lat: number,
    lon: number,
    zoom: number = 11
  ): Promise<{ dailyProb: number[]; monthlyDays: number[] } | null> {
    const n = 1 << zoom;
    const xExact = ((lon + 180.0) / 360.0) * n;
    const latRad = (lat * Math.PI) / 180.0;
    const yExact = (1.0 - Math.log(Math.tan(latRad) + 1.0 / Math.cos(latRad)) / Math.PI) / 2.0 * n;
    const tileX = Math.floor(xExact);
    const tileY = Math.floor(yExact);

    if (tileX < 0 || tileX >= n || tileY < 0 || tileY >= n) {
      return null;
    }

    const apiServer = getApiServer(manifest);
    const tileBase = manifest?.tile_base || 'tiles';
    const climatePath = manifest?.climate?.tile_path || 'climate_tiles';
    const cleanServer = apiServer.replace(/\/$/, '');
    let cleanPath = `${tileBase}/${climatePath}/${product}/${zoom}/${tileX}/${tileY}.bin`.replace(/\/+/g, '/');
    if (cleanPath.startsWith('/')) cleanPath = cleanPath.substring(1);
    const tileUrl = `${cleanServer}/${cleanPath}`;
    const authenticatedUrl = appendAuthToken(tileUrl);
    const token = authService.getSessionToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let buffer: ArrayBuffer | null = null;
    if (this.binaryTileCache.has(tileUrl)) {
      buffer = this.binaryTileCache.get(tileUrl)!;
    } else {
      try {
        const resp = await fetch(authenticatedUrl, {
          credentials: 'include',
          headers
        });
        if (resp.ok) {
          let rawBuffer = await resp.arrayBuffer();
          if (rawBuffer.byteLength >= 2) {
            const headerBytes = new Uint8Array(rawBuffer, 0, 2);
            if (headerBytes[0] === 0x1f && headerBytes[1] === 0x8b) {
              if (typeof DecompressionStream !== 'undefined') {
                const stream = new Response(rawBuffer).body?.pipeThrough(new DecompressionStream('gzip'));
                if (stream) {
                  rawBuffer = await new Response(stream).arrayBuffer();
                }
              }
            }
          }
          buffer = rawBuffer;
          this.lastAccessDenial = null;
          this.binaryTileCache.set(tileUrl, buffer);
        } else {
          const denial = TierAccessService.parseTileAccessDenial(resp, 'registered');
          if (denial) {
            this.lastAccessDenial = denial;
          } else {
            this.binaryTileCache.set(tileUrl, null);
          }
        }
      } catch {
        this.binaryTileCache.set(tileUrl, null);
      }
    }

    if (!buffer || buffer.byteLength < 32) {
      return null;
    }

    const view = new DataView(buffer);
    const magicStr = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
    if (magicStr !== 'GWCS') return null;

    const gridW = view.getUint16(8, true);
    const gridH = view.getUint16(10, true);
    const minLat = view.getFloat32(16, true);
    const maxLat = view.getFloat32(20, true);
    const minLon = view.getFloat32(24, true);
    const maxLon = view.getFloat32(28, true);

    if (lat < minLat || lat > maxLat || lon < minLon || lon > maxLon) {
      return null;
    }

    const px = Math.max(0, Math.min(gridW - 1, Math.floor((lon - minLon) / (maxLon - minLon) * gridW)));
    const py = Math.max(0, Math.min(gridH - 1, Math.floor((maxLat - lat) / (maxLat - minLat) * gridH)));
    const cellIdx = py * gridW + px;
    const numCells = gridW * gridH;

    const dailyOffset = 32;
    const monthlyOffset = 32 + 365 * numCells;

    const dailyArr = new Uint8Array(buffer, dailyOffset, 365 * numCells);
    const monthlyArr = new Uint8Array(buffer, monthlyOffset, 12 * numCells);

    const dailyProb: number[] = [];
    for (let doy = 1; doy <= 365; doy++) {
      const idx = (doy - 1) * numCells + cellIdx;
      dailyProb.push(dailyArr[idx]);
    }

    const monthlyDays: number[] = [];
    for (let m = 0; m < 12; m++) {
      const idx = m * numCells + cellIdx;
      monthlyDays.push(monthlyArr[idx]);
    }

    return { dailyProb, monthlyDays };
  }
}
export default ClimateService;
